import React, { useState, useEffect, useRef } from 'react';
import * as S from './Footer.styles';
import FacebookIcon from '@mui/icons-material/Facebook';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import EventIcon from '@mui/icons-material/Event';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { CDN_BASE_URL } from '../../config';

const Footer = () => {
  const [qrOpen, setQrOpen] = useState(null); // 点击状态
  const [hoverOpen, setHoverOpen] = useState(null); // 悬停状态
  const footerRef = useRef(null);

  // 预加载图片
  useEffect(() => {
    const imagesToPreload = [
      `${CDN_BASE_URL}/static/qrcode/whatsapp.png`,
      `${CDN_BASE_URL}/static/qrcode/instagram.png`,
      `${CDN_BASE_URL}/static/qrcode/rednote.png`,
      `${CDN_BASE_URL}/static/qrcode/wechat.jpg`
    ];

    imagesToPreload.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // 点击外部关闭二维码
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (footerRef.current && !footerRef.current.contains(event.target)) {
        setQrOpen(null);
      }
    };

    if (qrOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [qrOpen]);

  // 检查是否是移动设备
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  // 点击二维码按钮时的处理
  const handleQrClick = (key) => {
    setQrOpen(prev => (prev === key ? null : key));
  };

  // 鼠标进入处理（仅限桌面端）
  const handleMouseEnter = (key) => {
    if (!isMobile()) {
      setHoverOpen(key);
    }
  };

  // 鼠标离开处理（仅限桌面端）
  const handleMouseLeave = () => {
    if (!isMobile()) {
      setHoverOpen(null);
    }
  };

  // 获取二维码显示状态（悬停优先，点击次之）
  const getQRState = (key) => {
    // 悬停状态优先显示
    if (hoverOpen === key) {
      return true;
    }
    // 如果没有悬停任何项，则显示点击的项
    if (hoverOpen === null && qrOpen === key) {
      return true;
    }
    return false;
  };

  return (
    <S.FooterContainer ref={footerRef}>
      <S.FooterWrapper>
        {/* About Us Section */}
        <S.FooterSection>
          <S.Logo>
            <S.FooterLogo src={`${CDN_BASE_URL}/static/logo.png`} alt="Uplus Education" />
            <p>Prepare your child for a brighter future</p>
          </S.Logo>
          <S.SectionTitle>About Us</S.SectionTitle>
          <S.LinkList>
            <S.FooterLink href="/careers">
              Join Us
            </S.FooterLink>
            <S.FooterLink href="/team">
              Our Team
            </S.FooterLink>
            <S.FooterLink href="/courses">
              Our Courses
            </S.FooterLink>
            <S.FooterLink href="/timetable">
              <EventIcon style={{ fontSize: '1.1rem', color: '#3498db' }} />
              Timetables
            </S.FooterLink>
          </S.LinkList>
        </S.FooterSection>

        {/* Contact Us Section */}
        <S.FooterSection>
          <S.SectionTitle>Contact Us</S.SectionTitle>
          <S.ContactInfo>
            <S.ContactItem>
              <EmailIcon style={{ fontSize: '1.1rem', color: '#3498db', marginTop: '0.2rem', minWidth: 20 }} />
              <div>
                <div>Email us at:</div>
                <S.FooterLink href="mailto:info@uplusedu.com.au">
                  info@uplusedu.com.au
                </S.FooterLink>
              </div>
            </S.ContactItem>
            
            <S.ContactItem>
              <LocationOnIcon style={{ fontSize: '1.1rem', color: '#3498db', marginTop: '0.2rem', minWidth: 20 }} />
              <div>
                <div><strong>Victoria Park Campus</strong></div>
                <div>Unit 4/786 Albany Hwy</div>
                <div>East Victoria Park WA 6101</div>
              </div>
            </S.ContactItem>
            
            <S.ContactItem>
              <LocationOnIcon style={{ fontSize: '1.1rem', color: '#3498db', marginTop: '0.2rem', minWidth: 20 }} />
              <div>
                <div><strong>Claremont Campus</strong></div>
                <div>Level 1/229 Stirling Hwy</div>
                <div>Claremont WA 6010</div>
              </div>
            </S.ContactItem>
            
            <S.ContactItem>
              <LocationOnIcon style={{ fontSize: '1.1rem', color: '#3498db', marginTop: '0.2rem', minWidth: 20 }} />
              <div>
                <div><strong>Osborne Park Campus</strong></div>
                <div>342 Scarborough Beach Road</div>
                <div>Osborne Park WA 6017</div>
              </div>
            </S.ContactItem>
          </S.ContactInfo>
        </S.FooterSection>

        {/* Follow Us Section */}
        <S.FooterSection>
          <S.SectionTitle>Follow Us</S.SectionTitle>
          <S.SocialLinks>
            {/* Facebook 仍然跳转 */}
            <S.SocialItem href="https://www.facebook.com/upluseducatuion2018/" target="_blank" rel="noopener noreferrer">
              <S.SocialIcon>
                <FacebookIcon style={{ fontSize: '1.3rem', color: '#3498db' }} />
              </S.SocialIcon>
              <S.SocialText>Facebook</S.SocialText>
            </S.SocialItem>
            
            {/* WhatsApp 持久二维码 */}
            <S.SocialItem 
              as="div" 
              style={{ cursor: 'pointer' }} 
              onClick={() => handleQrClick('whatsapp')}
              onMouseEnter={() => handleMouseEnter('whatsapp')}
              onMouseLeave={handleMouseLeave}
            >
              <S.SocialIcon>
                <WhatsAppIcon style={{ fontSize: '1.3rem', color: '#25D366' }} />
              </S.SocialIcon>
              <S.SocialText>WhatsApp</S.SocialText>
              <S.QRCodePreview 
                className="qr-preview" 
                style={{ 
                  opacity: getQRState('whatsapp') ? 1 : 0, 
                  visibility: getQRState('whatsapp') ? 'visible' : 'hidden', 
                  transform: getQRState('whatsapp') ? 
                    (window.innerWidth <= 768 ? 'translateX(50%)' : 'translateY(-10px)') : 
                    (window.innerWidth <= 768 ? 'translateX(50%)' : 'translateY(-50%)')
                }}
              >
                <img src={`${CDN_BASE_URL}/static/qrcode/whatsapp.png`} alt="WhatsApp QR Code" />
              </S.QRCodePreview>
            </S.SocialItem>
            
            {/* Instagram 持久二维码 */}
            <S.SocialItem 
              as="div" 
              style={{ cursor: 'pointer' }} 
              onClick={() => handleQrClick('instagram')}
              onMouseEnter={() => handleMouseEnter('instagram')}
              onMouseLeave={handleMouseLeave}
            >
              <S.SocialIcon>
                <InstagramIcon style={{ fontSize: '1.3rem', color: '#E1306C' }} />
              </S.SocialIcon>
              <S.SocialText>Instagram</S.SocialText>
              <S.QRCodePreview 
                className="qr-preview" 
                style={{ 
                  opacity: getQRState('instagram') ? 1 : 0, 
                  visibility: getQRState('instagram') ? 'visible' : 'hidden', 
                  transform: getQRState('instagram') ? 
                    (window.innerWidth <= 768 ? 'translateX(50%)' : 'translateY(-10px)') : 
                    (window.innerWidth <= 768 ? 'translateX(50%)' : 'translateY(-50%)')
                }}
              >
                <img src={`${CDN_BASE_URL}/static/qrcode/instagram.png`} alt="Instagram QR Code" />
              </S.QRCodePreview>
            </S.SocialItem>
            
            {/* 小红书持久二维码 */}
            <S.SocialItem 
              as="div" 
              style={{ cursor: 'pointer' }} 
              onClick={() => handleQrClick('rednote')}
              onMouseEnter={() => handleMouseEnter('rednote')}
              onMouseLeave={handleMouseLeave}
            >
              <S.SocialIcon>
                <img src={`${CDN_BASE_URL}/static/icon/xiaohongshu.png`} alt="小红书" style={{ width: 21, height: 21 }} />
              </S.SocialIcon>
              <S.SocialText>RedNote</S.SocialText>
              <S.QRCodePreview 
                className="qr-preview" 
                style={{ 
                  opacity: getQRState('rednote') ? 1 : 0, 
                  visibility: getQRState('rednote') ? 'visible' : 'hidden', 
                  transform: getQRState('rednote') ? 
                    (window.innerWidth <= 768 ? 'translateX(50%)' : 'translateY(-10px)') : 
                    (window.innerWidth <= 768 ? 'translateX(50%)' : 'translateY(-50%)')
                }}
              >
                <img src={`${CDN_BASE_URL}/static/qrcode/rednote.png`} alt="小红书 QR Code" />
              </S.QRCodePreview>
            </S.SocialItem>
            
            {/* 微信持久二维码 */}
            <S.SocialItem 
              as="div" 
              style={{ cursor: 'pointer' }} 
              onClick={() => handleQrClick('wechat')}
              onMouseEnter={() => handleMouseEnter('wechat')}
              onMouseLeave={handleMouseLeave}
            >
              <S.SocialIcon>
                <img src={`${CDN_BASE_URL}/static/icon/wechat.png`} alt="WeChat" style={{ width: 21, height: 21 }} />
              </S.SocialIcon>
              <S.SocialText>WeChat</S.SocialText>
              <S.QRCodePreview 
                className="qr-preview" 
                style={{ 
                  opacity: getQRState('wechat') ? 1 : 0, 
                  visibility: getQRState('wechat') ? 'visible' : 'hidden', 
                  transform: getQRState('wechat') ? 
                    (window.innerWidth <= 768 ? 'translateX(50%)' : 'translateY(-10px)') : 
                    (window.innerWidth <= 768 ? 'translateX(50%)' : 'translateY(-50%)')
                }}
              >
                <img src={`${CDN_BASE_URL}/static/qrcode/wechat.jpg`} alt="WeChat QR Code" />
              </S.QRCodePreview>
            </S.SocialItem>
          </S.SocialLinks>
        </S.FooterSection>
      </S.FooterWrapper>

      <S.Copyright>
        <p>© 2025 Uplus Education. All rights reserved.</p>
      </S.Copyright>
    </S.FooterContainer>
  );
};

export default Footer;